
import { Vue, Component, Watch } from 'vue-property-decorator'
import Column from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import axios from 'axios'
import NewsItem, { newStates, statusClasses } from '@/types/newsItem'
import ColumnOptions from '@/components/listview/ColumnOptions.vue'
import PageHeader from '@/components/base/PageHeader.vue'
import SearchableColumnFilter from '@/components/base/SearchableColumnFilter.vue'
import ls from '@/ls'
import { can } from '@/globalFunctions'

@Component({
  methods: { can },
  components: { SearchableColumnFilter, PageHeader, ColumnOptions, ActiveFilters }
})

export default class News extends Vue {
  statusClasses = statusClasses
  settings = {
    routeNames: { create: 'newsCreate', show: 'newsShow', edit: 'newsEdit' },
    routeParamKey: 'newsItemId',
    permissionNames: { show: 'news.view', edit: 'news.edit', delete: 'news.delete' },
    functionNames: { getList: 'getNewsItemList' },
    getterNames: { list: 'newsItemList', loading: 'newsItemLoading' },
    localStorageName: 'newsItemTableSettings'
  }

  sortOrder = { column: 'id', order: 'descend' }
  conditions = {
    status: [newStates.Gepubliceerd]
  }

  localSettings = ls.get(this.settings.localStorageName)
  tableSettings:TableSettings = TableSettings.from(this.localSettings, this.conditions, this.sortOrder)

  columns:Column[] = [
    {
      title: 'Onderwerp',
      key: 'title',
      dataIndex: 'title',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Project',
      key: 'project.id',
      dataIndex: 'project',
      filters: [],
      scopedSlots: { filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Bouwnummers',
      key: 'buildings',
      dataIndex: 'buildings',
      ellipsis: true
    },
    {
      title: 'Publicatiedatum',
      key: 'publish_at',
      dataIndex: 'published_at',
      scopedSlots: { customRender: 'created_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      width: 160
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      scopedSlots: { customRender: 'status' },
      filters: [
        {
          text: 'Concept',
          value: newStates.Concept
        },
        {
          text: 'Gepubliceerd',
          value: newStates.Gepubliceerd
        },
        {
          text: 'Ingepland',
          value: newStates.Ingepland
        }
      ],

      sorter: false,
      sortDirections: ['ascend', 'descend'],
      width: 160
    },
    {
      title: 'Actie\'s',
      key: 'action',
      scopedSlots: { customRender: 'action' },
      width: 100
    }
  ]

  selectedRowKeys:number[] = []
  filtersLoaded = false

  mounted () : void {
    this.reload()
    this.getProjectFilter()
    this.onTableSettingsChange()
  }

  getProjectFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/project/news_items', { textColumn: 'name', valueColumn: 'id' }).then((res) => {
      this.columns[1].filters = res.data
    })
  }

  get activeColumns (): Column[] {
    return this.columns.filter(col => !this.$store.getters.InactiveNewsItemColumns.includes(col.key))
  }

  reload () : void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  async getFilters () : Promise<void> {
    this.filtersLoaded = true
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.reload()
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.reload()
  }

  // Required for ActiveFilters component
  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}, archived:boolean, unrelated:boolean) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = filters
    this.tableSettings.archived = archived
    this.tableSettings.unrelated = unrelated
    this.reload()
  }

  filterVisible = false
  filterDropdownVisibleChange (visible:boolean) : void {
    this.filterVisible = visible
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          if (!this.selectedRowKeys.length) {
            const keyboard = window.event as KeyboardEvent
            if (can(this.settings.permissionNames.edit)) {
              this.handleRouting(this.settings.routeNames.edit, keyboard, rowData)
            } else if (can(this.settings.permissionNames.show)) {
              this.handleRouting(this.settings.routeNames.show, keyboard, rowData)
            }
          } else {
            this.selectedRowKeys.includes(rowData.id) ? this.selectedRowKeys.splice(this.selectedRowKeys.indexOf(rowData.id), 1) : this.selectedRowKeys.push(rowData.id)
          }
        }
      }
    }
  }

  private handleRouting (routeName:string, keyboard: KeyboardEvent, rowData: { id: number }) {
    if (keyboard.ctrlKey) {
      const route = this.$router.resolve({
        name: routeName,
        params: { [this.settings.routeParamKey]: rowData.id.toString() }
      })
      window.open(route.href, '_blank')
    } else {
      this.$router.push({
        name: routeName,
        params: { [this.settings.routeParamKey]: rowData.id.toString() }
      })
    }
  }

  onSelectChange (selectedRowKeys:number[]) : void {
    this.selectedRowKeys = selectedRowKeys
  }

  navigateTo (routeName:string, id:number|undefined) : void {
    if (id) {
      this.$router.push({ name: routeName, params: { [this.settings.routeParamKey]: id.toString() } })
    } else {
      this.$router.push({ name: routeName })
    }
  }

  previousPage () : void {
    this.$router.go(-1)
  }

  toggleInactiveItems (e: { target: { checked: boolean } }) : void {
    this.tableSettings.archived = e.target.checked
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  get itemList () : { data: NewsItem[], meta: { per_page:string, total:number } } {
    return this.$store.getters[this.settings.getterNames.list]
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `Bouwnummer ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get loading () : boolean {
    return this.$store.getters[this.settings.getterNames.loading].list
  }

  get navigationCollapsed () : boolean {
    return this.$store.getters.navigationCollapsed
  }

  get screenWidth () : number {
    return screen.width
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
